import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import API from '../../services/api'; // Centralized API service
import styles from './LandingPage.module.css'; // Custom animations
import Footer from '../layout/Footer'; // THE REUSABLE FOOTER
import Navbar from '../layout/Navbar';

const LandingPage = () => {
    // --- EXISTING REACT LOGIC (PRESERVED) ---
    const formRef = useRef(null);
    const location = useLocation();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState(null);

    const [submissionState, setSubmissionState] = useState({
        statusType: null,
        message: '',
        isError: false,
    });
    const [firstName, setFirstName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const recaptchaRef = useRef(null);

    // --- LOGIC MIGRATED FROM NEW HTML ---
    const typedTextRef = useRef(null);
    const audioContextRef = useRef(null);
    const lastScrollYRef = useRef(window.scrollY);
    const isScrollingDownRef = useRef(true);

    // useEffect for smooth scrolling (PRESERVED)
    useEffect(() => {
        if (location.hash === '#early-access') {
            formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [location]);

    // useEffect for zoom (PRESERVED)
    useEffect(() => {
        document.body.style.zoom = '0.9';
        return () => {
            document.body.style.zoom = '1';
        };
    }, []);

    // useEffect for reCAPTCHA validation (PRESERVED)
    useEffect(() => {
        if (recaptchaToken && submissionState.message === 'Please complete the CAPTCHA.') {
            setSubmissionState({ statusType: null, message: '', isError: false });
        }
    }, [recaptchaToken, submissionState.message]);


    // --- NEW: Typewriter Effect (Migrated from HTML <script>) ---
    useEffect(() => {
        const typedTextElement = typedTextRef.current;
        if (!typedTextElement) return;

        const textToType = "Finding Job Has Never Been Easier...";
        const typingSpeed = 120;
        const initialDelay = 1500;
        const spaceSoundDelay = 400;
        let charIndex = 0;

        function playTypeSound() {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const audioContext = audioContextRef.current;

            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            const frequencies = [1200, 1400, 1600];
            oscillator.frequency.setValueAtTime(frequencies[Math.floor(Math.random() * frequencies.length)], audioContext.currentTime);
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.08, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.08);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.08);
        }

        function type() {
            if (charIndex < textToType.length) {
                const currentChar = textToType.charAt(charIndex);
                typedTextElement.textContent += currentChar;

                if (currentChar !== ' ' && Math.random() > 0.3) {
                    playTypeSound();
                }

                charIndex++;

                const delay = (currentChar === ' ' || currentChar === '.') ? typingSpeed + spaceSoundDelay : typingSpeed;
                setTimeout(type, delay);
            }
        }

        typedTextElement.style.opacity = '0';
        typedTextElement.style.transform = 'translateY(10px)';
        typedTextElement.style.transition = 'opacity 0.8s ease-out, transform 0.8s ease-out';

        const startTimeout = setTimeout(() => {
            typedTextElement.style.opacity = '1';
            typedTextElement.style.transform = 'translateY(0)';
            const typeTimeout = setTimeout(type, 500);
            return () => clearTimeout(typeTimeout);
        }, initialDelay);

        return () => {
            clearTimeout(startTimeout);
            // Cleanup: Reset text content if component unmounts
            if (typedTextElement) {
                typedTextElement.textContent = '';
            }
        };
    }, []); // Empty dependency array ensures this runs once on mount

    // --- NEW: Scroll Reveal Effect (Migrated from HTML <script>) ---
    useEffect(() => {
        const animatedElements = document.querySelectorAll('.animate-on-scroll');

        const handleScroll = () => {
            isScrollingDownRef.current = window.scrollY > lastScrollYRef.current;
            lastScrollYRef.current = window.scrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const target = entry.target;
                if (entry.isIntersecting) {
                    if (!isScrollingDownRef.current) {
                        target.style.transitionDuration = '0ms';
                    } else {
                        target.style.transitionDuration = '800ms';
                    }
                    target.classList.add('opacity-100', 'translate-y-0');
                    target.classList.remove('opacity-0', 'translate-y-14'); // Use 14 to match HTML
                } else {
                    target.classList.remove('opacity-100', 'translate-y-0');
                    target.classList.add('opacity-0', 'translate-y-14');
                }
            });
        }, {
            threshold: 0.2
        });

        animatedElements.forEach(el => observer.observe(el));

        return () => {
            window.removeEventListener('scroll', handleScroll);
            animatedElements.forEach(el => observer.unobserve(el));
        };
    }, []); // Empty dependency array ensures this runs once on mount

    // --- Form submission handler (PRESERVED) ---
    const handleOnboardingSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;

        if (!recaptchaToken) {
            setSubmissionState({
                statusType: 'ERROR',
                message: 'Please complete the CAPTCHA.',
                isError: true,
            });
            return;
        }

        setIsSubmitting(true);
        setSubmissionState({ statusType: null, message: '', isError: false });

        try {
            const formData = { name, email, 'g-recaptcha-response': recaptchaToken };
            const response = await API.post('core/early-access/', formData);

            setFirstName(name.split(' ')[0] || 'friend');

            setSubmissionState({
                statusType: response.data.status_type,
                message: response.data.message,
                isError: false,
            });

            if (response.data.status_type === 'CREATED') {
                setName('');
                setEmail('');
            }
        } catch (error) {
            console.error("Form submission error:", error);
            if (error.response) {
                console.error("Validation Errors:", error.response.data);
            }
            const errorMessage = error.response?.data?.detail ||
                (typeof error.response?.data === 'object' ? Object.values(error.response.data).flat().join(' ') : 'An unexpected error occurred.');

            setSubmissionState({
                statusType: 'ERROR',
                message: errorMessage,
                isError: true,
            });
        } finally {
            setIsSubmitting(false);
            recaptchaRef.current?.reset();
            setRecaptchaToken(null);
        }
    };

    return (
        <div className="bg-white dark:bg-[#141414] text-black dark:text-[#D9D9D9] transition-colors duration-300 min-h-screen flex flex-col">
            <Navbar />

            <main className="flex-grow">
                {/* =================================================================
                == Typewriter HERO section
                ================================================================= */}
                <section className="flex items-center justify-center min-h-screen transition-colors duration-300 px-4 sm:px-6 md:px-12 lg:px-24 w-full">
                    <div className="w-full max-w-7xl">
                        <h1 id="hero-heading" aria-label="Finding Job Has Never Been Easier"
                            className={`text-4xl lg:text-6xl font-semibold text-black dark:text-[#D9D9D9] leading-tight ${styles.heroHeading}`}>
                            <span className={styles.typewriterContainer}>
                                <span id="typed-text" ref={typedTextRef}></span>
                                <span className={styles.typingCursor}></span>
                            </span>
                        </h1>
                    </div>
                </section>

                {/* =================================================================
                == section: Finding a job is messy
                ================================================================= */}
                <section className="min-h-screen py-16 px-4 lg:px-24 animate-on-scroll opacity-0 translate-y-14 transition-all ease-in-out duration-[1500ms]">
                    <div className="max-w-7xl mx-auto text-start">
                        <div className="lg:text-6xl text-4xl font-semibold text-gray-900 dark:text-white">Finding a job is messy</div>
                        <p className="lg:text-xl text-base font-medium text-slate-500 dark:text-gray-400 leading-tight mt-3">You have to check multiple <span className="text-black dark:text-white">websites</span> or <span className="text-black dark:text-white">platforms</span>.</p>
                        <div className="flex flex-col sm:flex-row justify-between gap-8 md:gap-12 mt-10">
                            <div className="flex flex-col items-center text-center group transition-all duration-300 ease-in-out hover:scale-105 w-full md:w-1/3">
                                <img className="lg:h-72 h-64 w-full object-contain transition-transform duration-300 group-hover:scale-110" src="./static/landing-page/career-page-sample.svg" alt="Career Pages" />
                                <div className="text-lg lg:text-xl mt-6 h-20 flex items-start justify-center text-center text-gray-900 dark:text-[#D9D9D9] group-hover:text-sky-500 dark:group-hover:text-sky-400 transition-colors duration-300">
                                    Official career pages
                                </div>
                            </div>
                            <div className="flex flex-col items-center text-center group transition-all duration-300 ease-in-out hover:scale-105 w-full md:w-1/3">
                                <img className="lg:h-72 h-64 w-full object-contain transition-transform duration-300 group-hover:scale-110" src="./static/landing-page/social-media-sample.svg" alt="Social Media" />
                                <div className="text-lg lg:text-xl mt-6 h-20 flex items-start justify-center text-center text-gray-900 dark:text-[#D9D9D9] group-hover:text-sky-500 dark:group-hover:text-sky-400 transition-colors duration-300">
                                    Their social media pages
                                </div>
                            </div>
                            <div className="flex flex-col items-center text-center group transition-all duration-300 ease-in-out hover:scale-105 w-full md:w-1/3">
                                <img className="lg:h-72 h-64 w-full object-contain transition-transform duration-300 group-hover:scale-110" src="./static/landing-page/job-platform-sample.svg" alt="Job Platforms" />
                                <div className="text-lg lg:text-xl mt-6 h-20 flex items-start justify-center text-center text-gray-900 dark:text-[#D9D9D9] group-hover:text-sky-500 dark:group-hover:text-sky-400 transition-colors duration-300">
                                    Other generic job-finding platforms
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =================================================================
                == Section: There are also many other problems
                ================================================================= */}
                <section className="lg:pb-52 md:pb-52 pb-20 px-4 lg:px-24">
                    <div className="max-w-7xl mx-auto text-start space-y-8">
                        <div className="lg:text-6xl text-4xl font-semibold text-gray-900 dark:text-[#F5F5F7] leading-tight tracking-tight">
                            There are also many other problems
                            <br />
                            <span className="bg-gradient-to-r from-gray-900 to-gray-600 dark:from-[#F5F5F7] dark:to-[#A2A2A5] bg-clip-text text-transparent">
                                in current job finding ecosystem.
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <div className="border rounded-full py-4 px-8 lg:text-xl text-lg hover:scale-105 duration-500 ease-in-out flex items-center justify-between group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-all">
                                    <div className="flex items-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                                            <path d="M10.4057 23C7.88703 21.3902 -1.20099 16.0218 1.49436 11.4887C4.86354 5.82223 9.59688 -0.596814 13.352 1.35853C17.1071 3.31388 24.9287 9.3074 22.5621 12.517C20.6688 15.0846 16.8309 20.5343 14.6615 22.4634M11.3354 17.6557C11.6262 18.0077 12.1049 18.1089 12.4993 17.87C12.8861 17.6357 13.0701 17.1482 12.9755 16.6931M11.3354 17.6557C11.2833 17.5927 11.2373 17.5215 11.1988 17.4429C10.9456 16.9253 11.1175 16.2774 11.5826 15.9957C12.0477 15.714 12.63 15.9052 12.8832 16.4228C12.9258 16.51 12.9564 16.6008 12.9755 16.6931M11.3354 17.6557C11.7592 17.8257 12.6805 17.871 12.9755 16.6931M11.7181 13.9535C11.0895 10.9323 10.4133 6 11.8689 6C13.3245 6 12.6483 10.9323 12.0197 13.9535C11.978 14.1538 11.7597 14.1538 11.7181 13.9535Z" className="stroke-gray-400 group-hover:stroke-red-400 transition-colors duration-300" strokeLinecap="round" />
                                        </svg>
                                        <span className="pl-3 text-gray-950 dark:text-gray-300">Missed opportunities </span>
                                    </div>
                                </div>
                                <div className="border rounded-full py-4 px-8 lg:text-xl text-lg hover:scale-105 duration-500 ease-in-out flex items-center justify-between group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-all">
                                    <div className="flex items-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                                            <path d="M10.4057 23C7.88703 21.3902 -1.20099 16.0218 1.49436 11.4887C4.86354 5.82223 9.59688 -0.596814 13.352 1.35853C17.1071 3.31388 24.9287 9.3074 22.5621 12.517C20.6688 15.0846 16.8309 20.5343 14.6615 22.4634M11.3354 17.6557C11.6262 18.0077 12.1049 18.1089 12.4993 17.87C12.8861 17.6357 13.0701 17.1482 12.9755 16.6931M11.3354 17.6557C11.2833 17.5927 11.2373 17.5215 11.1988 17.4429C10.9456 16.9253 11.1175 16.2774 11.5826 15.9957C12.0477 15.714 12.63 15.9052 12.8832 16.4228C12.9258 16.51 12.9564 16.6008 12.9755 16.6931M11.3354 17.6557C11.7592 17.8257 12.6805 17.871 12.9755 16.6931M11.7181 13.9535C11.0895 10.9323 10.4133 6 11.8689 6C13.3245 6 12.6483 10.9323 12.0197 13.9535C11.978 14.1538 11.7597 14.1538 11.7181 13.9535Z" className="stroke-gray-400 group-hover:stroke-red-400 transition-colors duration-300" strokeLinecap="round" />
                                        </svg>
                                        <span className="pl-3 text-gray-950 dark:text-gray-300">Lots of unwanted job recommendations</span>
                                    </div>
                                </div>
                                <div className="border rounded-full py-4 px-8 lg:text-xl text-lg hover:scale-105 duration-500 ease-in-out flex items-center justify-between group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-all">
                                    <div className="flex items-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                                            <path d="M10.4057 23C7.88703 21.3902 -1.20099 16.0218 1.49436 11.4887C4.86354 5.82223 9.59688 -0.596814 13.352 1.35853C17.1071 3.31388 24.9287 9.3074 22.5621 12.517C20.6688 15.0846 16.8309 20.5343 14.6615 22.4634M11.3354 17.6557C11.6262 18.0077 12.1049 18.1089 12.4993 17.87C12.8861 17.6357 13.0701 17.1482 12.9755 16.6931M11.3354 17.6557C11.2833 17.5927 11.2373 17.5215 11.1988 17.4429C10.9456 16.9253 11.1175 16.2774 11.5826 15.9957C12.0477 15.714 12.63 15.9052 12.8832 16.4228C12.9258 16.51 12.9564 16.6008 12.9755 16.6931M11.3354 17.6557C11.7592 17.8257 12.6805 17.871 12.9755 16.6931M11.7181 13.9535C11.0895 10.9323 10.4133 6 11.8689 6C13.3245 6 12.6483 10.9323 12.0197 13.9535C11.978 14.1538 11.7597 14.1538 11.7181 13.9535Z" className="stroke-gray-400 group-hover:stroke-red-400 transition-colors duration-300" strokeLinecap="round" />
                                        </svg>
                                        <span className="pl-3 text-gray-950 dark:text-gray-300">Basic filtering</span>
                                    </div>
                                </div>
                                <div className="border rounded-full py-4 px-8 lg:text-xl text-lg hover:scale-105 duration-500 ease-in-out flex items-center justify-between group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-all">
                                    <div className="flex items-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                                            <path d="M10.4057 23C7.88703 21.3902 -1.20099 16.0218 1.49436 11.4887C4.86354 5.82223 9.59688 -0.596814 13.352 1.35853C17.1071 3.31388 24.9287 9.3074 22.5621 12.517C20.6688 15.0846 16.8309 20.5343 14.6615 22.4634M11.3354 17.6557C11.6262 18.0077 12.1049 18.1089 12.4993 17.87C12.8861 17.6357 13.0701 17.1482 12.9755 16.6931M11.3354 17.6557C11.2833 17.5927 11.2373 17.5215 11.1988 17.4429C10.9456 16.9253 11.1175 16.2774 11.5826 15.9957C12.0477 15.714 12.63 15.9052 12.8832 16.4228C12.9258 16.51 12.9564 16.6008 12.9755 16.6931M11.3354 17.6557C11.7592 17.8257 12.6805 17.871 12.9755 16.6931M11.7181 13.9535C11.0895 10.9323 10.4133 6 11.8689 6C13.3245 6 12.6483 10.9323 12.0197 13.9535C11.978 14.1538 11.7597 14.1538 11.7181 13.9535Z" className="stroke-gray-400 group-hover:stroke-red-400 transition-colors duration-300" strokeLinecap="round" />
                                        </svg>
                                        <span className="pl-3 text-gray-950 dark:text-gray-300">Basic job recommendations</span>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="border rounded-full py-4 px-8 lg:text-xl text-lg hover:scale-105 duration-500 ease-in-out flex items-center justify-between group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-all">
                                    <div className="flex items-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                                            <path d="M10.4057 23C7.88703 21.3902 -1.20099 16.0218 1.49436 11.4887C4.86354 5.82223 9.59688 -0.596814 13.352 1.35853C17.1071 3.31388 24.9287 9.3074 22.5621 12.517C20.6688 15.0846 16.8309 20.5343 14.6615 22.4634M11.3354 17.6557C11.6262 18.0077 12.1049 18.1089 12.4993 17.87C12.8861 17.6357 13.0701 17.1482 12.9755 16.6931M11.3354 17.6557C11.2833 17.5927 11.2373 17.5215 11.1988 17.4429C10.9456 16.9253 11.1175 16.2774 11.5826 15.9957C12.0477 15.714 12.63 15.9052 12.8832 16.4228C12.9258 16.51 12.9564 16.6008 12.9755 16.6931M11.3354 17.6557C11.7592 17.8257 12.6805 17.871 12.9755 16.6931M11.7181 13.9535C11.0895 10.9323 10.4133 6 11.8689 6C13.3245 6 12.6483 10.9323 12.0197 13.9535C11.978 14.1538 11.7597 14.1538 11.7181 13.9535Z" className="stroke-gray-400 group-hover:stroke-red-400 transition-colors duration-300" strokeLinecap="round" />
                                        </svg>
                                        <span className="pl-3 text-gray-950 dark:text-gray-300">Expensive fees</span>
                                    </div>
                                </div>
                                <div className="border rounded-full py-4 px-8 lg:text-xl text-lg hover:scale-105 duration-500 ease-in-out flex items-center justify-between group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-all">
                                    <div className="flex items-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                                            <path d="M10.4057 23C7.88703 21.3902 -1.20099 16.0218 1.49436 11.4887C4.86354 5.82223 9.59688 -0.596814 13.352 1.35853C17.1071 3.31388 24.9287 9.3074 22.5621 12.517C20.6688 15.0846 16.8309 20.5343 14.6615 22.4634M11.3354 17.6557C11.6262 18.0077 12.1049 18.1089 12.4993 17.87C12.8861 17.6357 13.0701 17.1482 12.9755 16.6931M11.3354 17.6557C11.2833 17.5927 11.2373 17.5215 11.1988 17.4429C10.9456 16.9253 11.1175 16.2774 11.5826 15.9957C12.0477 15.714 12.63 15.9052 12.8832 16.4228C12.9258 16.51 12.9564 16.6008 12.9755 16.6931M11.3354 17.6557C11.7592 17.8257 12.6805 17.871 12.9755 16.6931M11.7181 13.9535C11.0895 10.9323 10.4133 6 11.8689 6C13.3245 6 12.6483 10.9323 12.0197 13.9535C11.978 14.1538 11.7597 14.1538 11.7181 13.9535Z" className="stroke-gray-400 group-hover:stroke-red-400 transition-colors duration-300" strokeLinecap="round" />
                                        </svg>
                                        <span className="pl-3 text-gray-950 dark:text-gray-300">No proper control over the platform</span>
                                    </div>
                                </div>
                                <div className="border rounded-full py-4 px-8 lg:text-xl text-lg hover:scale-105 duration-500 ease-in-out flex items-center justify-between group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-all">
                                    <div className="flex items-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                                            <path d="M10.4057 23C7.88703 21.3902 -1.20099 16.0218 1.49436 11.4887C4.86354 5.82223 9.59688 -0.596814 13.352 1.35853C17.1071 3.31388 24.9287 9.3074 22.5621 12.517C20.6688 15.0846 16.8309 20.5343 14.6615 22.4634M11.3354 17.6557C11.6262 18.0077 12.1049 18.1089 12.4993 17.87C12.8861 17.6357 13.0701 17.1482 12.9755 16.6931M11.3354 17.6557C11.2833 17.5927 11.2373 17.5215 11.1988 17.4429C10.9456 16.9253 11.1175 16.2774 11.5826 15.9957C12.0477 15.714 12.63 15.9052 12.8832 16.4228C12.9258 16.51 12.9564 16.6008 12.9755 16.6931M11.3354 17.6557C11.7592 17.8257 12.6805 17.871 12.9755 16.6931M11.7181 13.9535C11.0895 10.9323 10.4133 6 11.8689 6C13.3245 6 12.6483 10.9323 12.0197 13.9535C11.978 14.1538 11.7597 14.1538 11.7181 13.9535Z" className="stroke-gray-400 group-hover:stroke-red-400 transition-colors duration-300" strokeLinecap="round" />
                                        </svg>
                                        <span className="pl-3 text-gray-950 dark:text-gray-300">Implemented AI in wrong and chaotic way</span>
                                    </div>
                                </div>
                                <div className="border rounded-full py-4 px-8 lg:text-xl text-lg hover:scale-105 duration-500 ease-in-out flex items-center justify-between group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-all">
                                    <div className="flex items-center">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform duration-300">
                                            <path d="M10.4057 23C7.88703 21.3902 -1.20099 16.0218 1.49436 11.4887C4.86354 5.82223 9.59688 -0.596814 13.352 1.35853C17.1071 3.31388 24.9287 9.3074 22.5621 12.517C20.6688 15.0846 16.8309 20.5343 14.6615 22.4634M11.3354 17.6557C11.6262 18.0077 12.1049 18.1089 12.4993 17.87C12.8861 17.6357 13.0701 17.1482 12.9755 16.6931M11.3354 17.6557C11.2833 17.5927 11.2373 17.5215 11.1988 17.4429C10.9456 16.9253 11.1175 16.2774 11.5826 15.9957C12.0477 15.714 12.63 15.9052 12.8832 16.4228C12.9258 16.51 12.9564 16.6008 12.9755 16.6931M11.3354 17.6557C11.7592 17.8257 12.6805 17.871 12.9755 16.6931M11.7181 13.9535C11.0895 10.9323 10.4133 6 11.8689 6C13.3245 6 12.6483 10.9323 12.0197 13.9535C11.978 14.1538 11.7597 14.1538 11.7181 13.9535Z" className="stroke-gray-400 group-hover:stroke-red-400 transition-colors duration-300" strokeLinecap="round" />
                                        </svg>
                                        <span className="pl-3 text-gray-950 dark:text-gray-300">Too many pages to track</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =================================================================
                == section: Introducing Lighthouse
                ================================================================= */}
                <section className="min-h-screen py-16 px-4 lg:px-24 bg-transparent">
                    <div className="max-w-7xl mx-auto">
                        {/* Header */}
                        <div className="mb-10">
                            <div className="lg:text-6xl text-4xl font-semibold text-gray-900 dark:text-gray-100 leading-snug tracking-tight">
                                Introducing
                                <span className="relative inline-block mx-4">
                                    <span className="bg-gradient-to-r from-[#3e9bff] via-[#cf8f9c] to-[#af8ecb] bg-clip-text text-transparent relative z-10">
                                        "Lighthouse"
                                    </span>
                                    <span className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-pink-300/20 to-purple-400/20 blur-xl scale-125 z-0"
                                        style={{ animation: "pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite" }}></span>
                                </span>
                                , Trueway's AI that changes how you find jobs.
                            </div>
                            <p className="lg:text-xl text-lg font-medium text-slate-500 dark:text-gray-400 leading-tight mt-3"><span className="text-black dark:text-blue-400">Job searching will never be the same again. </span> Find your job here, then stay updated and explore new opportunities.</p>
                        </div>

                        <div className="space-y-16 border-l border-gray-200 dark:border-gray-700 border-r pt-5">
                            {/* Feature 1 */}
                            <div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                                <div className="flex items-center py-4">
                                    <div className="flex-shrink-0 mr-6">
                                        <div className="w-20 h-20 rounded-full bg-[#E5E5E5] flex items-center justify-center">
                                            <img src="./static/landing-page/feature-1.svg" className="w-1/2 h-auto" alt="Feature Logo" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="lg:text-xl text-lg font-medium text-gray-900 dark:text-gray-100">No missed opportunities. We have reimagined how we do job recommendations, matching, and almost everything else.</h3>
                                    </div>
                                </div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                            </div>

                            {/* Feature 2 */}
                            <div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                                <div className="flex items-center py-4">
                                    <div className="flex-shrink-0 mr-6">
                                        <div className="w-20 h-20 rounded-full bg-[#E5E5E5] flex items-center justify-center">
                                            <img src="./static/landing-page/feature-2.svg" className="w-1/2 h-auto" alt="Feature Logo" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="lg:text-xl text-lg font-medium text-gray-900 dark:text-gray-100">Automate your job-finding process in the most effective way.</h3>
                                    </div>
                                </div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                            </div>

                            {/* Feature 3 */}
                            <div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                                <div className="flex items-center py-4">
                                    <div className="flex-shrink-0 mr-6">
                                        <div className="w-20 h-20 rounded-full bg-[#E5E5E5] flex items-center justify-center">
                                            <img src="./static/landing-page/feature-3.svg" className="w-1/2 h-auto" alt="Feature Logo" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="lg:text-xl text-lg font-medium text-gray-900 dark:text-gray-100">Wherever you need to find a job, you now can.</h3>
                                    </div>
                                </div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                            </div>

                            {/* Feature 4 */}
                            <div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                                <div className="flex items-center py-4">
                                    <div className="flex-shrink-0 mr-6">
                                        <div className="w-20 h-20 rounded-full bg-[#E5E5E5] flex items-center justify-center">
                                            <img src="./static/landing-page/feature-5.svg" className="w-1/2 h-auto" alt="Feature Logo" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="lg:text-xl text-lg font-medium text-gray-900 dark:text-gray-100">AI is at the core, deeply integrated but very noiseless.</h3>
                                    </div>
                                </div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                            </div>

                            {/* Feature 5 */}
                            <div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                                <div className="flex items-center py-4">
                                    <div className="flex-shrink-0 mr-6">
                                        <div className="w-20 h-20 rounded-full bg-[#E5E5E5] flex items-center justify-center">
                                            <img src="./static/landing-page/feature-6.svg" className="w-1/2 h-auto" alt="Feature Logo" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="lg:text-xl text-lg font-medium text-gray-900 dark:text-gray-100">You control everything. You'll build every behavior from scratch. Cool, huh!</h3>
                                    </div>
                                </div>
                                <div className="border border-gray-200 dark:border-gray-700"></div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =================================================================
                == section: stay tuned
                ================================================================= */}
                <section className="py-24 flex items-center">
                    <div className="max-w-7xl mx-auto text-start space-y-8 px-4 w-full">
                        <div className="lg:text-6xl text-4xl font-semibold text-gray-900 dark:text-[#D9D9D9] leading-tight">
                            <span style={{ backgroundImage: 'linear-gradient(180deg, #004C94 45%, #297BC4 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'rgba(0, 0, 0, 0)', backgroundClip: 'text', textFillColor: 'transparent' }}>Stay tuned</span> with Trueway.  <br />
                            We're transforming job discovery—<span className="italic font-extrabold">forever</span>.
                        </div>
                        <div>
                            <div className="mx-auto">
                                <div className="flex flex-col md:flex-row justify-between gap-12 mt-10">
                                    <div className="flex flex-col items-center text-center group transition-all duration-300 ease-in-out hover:scale-105 w-full md:w-1/3">
                                        <img src="./static/landing-page/cheap.svg" alt="Affordable" className="h-64 w-full object-contain transition-transform duration-300 group-hover:scale-110" />
                                        <div className="text-lg lg:text-xl mt-6 h-20 flex items-start justify-center text-center text-gray-900 dark:text-[#D9D9D9] group-hover:text-sky-500 dark:group-hover:text-sky-400 transition-colors duration-300">
                                            More affordable. Surprisingly powerful.
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-center text-center group transition-all duration-300 ease-in-out hover:scale-105 w-full md:w-1/3">
                                        <img src="./static/landing-page/one-tool.svg" alt="One Tool" className="h-64 w-full object-contain transition-transform duration-300 group-hover:scale-110" />
                                        <div className="text-lg lg:text-xl mt-6 h-20 flex items-start justify-center text-center text-gray-900 dark:text-[#D9D9D9] group-hover:text-sky-500 dark:group-hover:text-sky-400 transition-colors duration-3Examples">
                                            You only need one tool to find jobs and stay updated.
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-center text-center group transition-all duration-300 ease-in-out hover:scale-105 w-full md:w-1/3">
                                        <img src="./static/landing-page/building-trueway.png" alt="Reimagining" className="h-64 w-full object-contain transition-transform duration-300 group-hover:scale-110" />
                                        <div className="text-lg lg:text-xl mt-6 h-20 flex items-start justify-center text-center text-gray-900 dark:text-[#D9D9D9] group-hover:text-sky-500 dark:group-hover:text-sky-400 transition-colors duration-300">
                                            Reimagining the job-finding experience.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =================================================================
                == Last section: join in our early access
                ================================================================= */}
                <section id="early-access" ref={formRef} className="pt-20 pb-52 px-4 lg:px-24 flex flex-col justify-center">
                    <div className="w-full max-w-7xl mx-auto">
                        <div className="lg:text-6xl text-4xl font-semibold text-gray-900 dark:text-[#D9D9D9] leading-tight mb-8">Join Our Waitlist</div>
                        <div className="w-full space-y-4">
                            <form id="onboardingForm" onSubmit={handleOnboardingSubmit} className="flex flex-col lg:flex-row items-center gap-4 w-full">
                                <div className="relative w-full flex-grow">
                                    <input
                                        type="text"
                                        id="full_name"
                                        name="full_name"
                                        placeholder=" "
                                        className="block px-4 pb-2.5 pt-6 w-full text-base text-gray-900 bg-transparent rounded-2xl border-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-sky-500 focus:outline-none focus:ring-0 focus:border-sky-500 hover:border-black dark:hover:border-white peer transition-colors duration-300"
                                        required
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        disabled={isSubmitting}
                                    />
                                    <label
                                        htmlFor="full_name"
                                        className="absolute text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-5 z-10 origin-[0] start-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4"
                                    >
                                        Full Name
                                    </label>
                                </div>

                                <div className="relative w-full flex-grow">
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        placeholder=" "
                                        className="block px-4 pb-2.5 pt-6 w-full text-lg text-gray-900 bg-transparent rounded-2xl border-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-sky-500 focus:outline-none focus:ring-0 focus:border-sky-500 hover:border-black dark:hover:border-white peer transition-colors duration-300"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isSubmitting}
                                    />
                                    <label
                                        htmlFor="email"
                                        className="absolute text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-5 z-10 origin-[0] start-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4"
                                    >
                                        Your Email
                                    </label>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full lg:w-auto px-6 py-4 bg-white border-2 border-black text-black font-semibold dark:text-gray-950 rounded-2xl hover:bg-gray-100 dark:bg-gray-50 dark:hover:bg-gray-200 transition duration-200 flex-shrink-0 cursor-pointer disabled:opacity-50"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Applying...' : 'Apply'}
                                </button>
                            </form>

                            <p className="text-blue-600 dark:text-orange-300 text-sm">We will notify you when we release our very first version.</p>

                            {/* --- ReCAPTCHA Component (PRESERVED) --- */}
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                onChange={(token) => setRecaptchaToken(token)}
                                onExpired={() => setRecaptchaToken(null)}
                                className="mt-4"
                            />

                            {/* --- Form Feedback Section (PRESERVED & STATE-DRIVEN) --- */}
                            {submissionState.message && (
                                <div className={`${styles.animateFadeIn} mt-4`}>
                                    {/* --- RENDER ON ERROR --- */}
                                    {submissionState.isError && (
                                        <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-lg border border-red-200 dark:border-red-700 text-red-800 dark:text-red-100">
                                            <p>{submissionState.message}</p>
                                        </div>
                                    )}

                                    {/* --- RENDER ON NEW SIGNUP SUCCESS ('CREATED') --- */}
                                    {submissionState.statusType === 'CREATED' && (
                                        <div className="bg-amber-50 dark:bg-amber-900/10 p-6 rounded-lg border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-100">
                                            <div className="flex items-start gap-3">
                                                <svg className="w-5 h-5 text-amber-600 dark:text-amber-300 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                                <div>
                                                    <p className="font-medium">Welcome aboard, {firstName}!</p>
                                                    <p className="mt-1 text-sm">Thank you for joining Trueway's early access. We're honored to have you with us as we reinvent job discovery.</p>
                                                    <p className="mt-2 text-sm">Look out for an email from us in your inbox. If you can't find it, it might be in your spam folder.</p>
                                                    <p className="mt-2 text-xs italic text-amber-600 dark:text-amber-300">The Trueway Team</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* --- RENDER ON EXISTING SIGNUP ('ALREADY_EXISTS') --- */}
                                    {submissionState.statusType === 'ALREADY_EXISTS' && (
                                        <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-lg border border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-100">
                                            <div className="flex items-start gap-3">
                                                <svg className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                <div>
                                                    <p className="font-medium">Great news, {firstName}!</p>
                                                    <p className="mt-1 text-sm">You're already on our early access list. We haven't forgotten about you and we'll be in touch as soon as Trueway is ready.</p>
                                                    <p className="mt-2 text-xs italic text-blue-600 dark:text-blue-300">The Trueway Team</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default LandingPage;